/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:13:33 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { FixedRightSlidePanel } from "common/components";
import { DnDItemTypes } from "config";
import { getDefaultField } from "lib/factories";
import { ListEditorProvider, useListEditorContext } from "lib/providers";
import { FIELD_TYPES, FormField } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Settings as FSettingsIcon } from "react-feather";
import { COLORSCHEME } from "theme/styles";
import { useToggleState } from "ui-widgets";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import Skeleton from "@mui/material/Skeleton";
import { DragDropListItemWrapper, ListItemDragLayer, OSDropZone } from "ui-widgets/drag-and-drop";
import { GenericDropTarget } from "ui-widgets/drag-and-drop";
import { useFormFieldsAPI, useFieldTypesAPI } from "form-builder/services";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { IFieldDnDSelectorState, NewFieldDragLayer } from "./AddNewFieldToolSet";
import { EmptyFieldsCTA } from "./EmptyFieldsCTA";
import { getFieldTypeWidgets } from "./FieldTypeWidgets";
import { FieldForm } from "./FieldForm";
import { FieldPreview } from "./FieldPreview";

export const FieldsListEditor = () => {
  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);
  const { useReorderField } = useFormFieldsAPI();

  return (
    <>
      <NewFieldDragLayer />
      {/** Placing drag layer here to avoid it being affected by the fixed css position of the left panel. Apparently there is a know source of unpredictable behaviour with children of elements that have fixed css position - https://stackoverflow.com/questions/21091958/css-fixed-child-element-positions-relative-to-parent-element-not-to-the-viewpo  */}
      {currentFormConfiguration?.id && (
        <ListEditorProvider<FormField, true>
          uniqueIdentifier="id"
          positionLabel="order"
          useAPIReorderListItem={useReorderField}
          apiServicePath={APISERVICEPATHS.formFields}
          apiQueryParams={{ form: currentFormConfiguration.id }}
          dependencyList={[currentFormConfiguration.id]}
        >
          <FieldsList />
        </ListEditorProvider>
      )}
    </>
  );
};

const FieldsList = () => {
  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);
  const addForm = useFormBuilderStore((state) => state.addForm);

  const {
    createNewItem,
    itemsListState,
    selectItem,
    findItem,
    isFetchingItems,
    fetchItemsHasError,
    fetchItemsStatusCode,
    creationQueueHasItems,
    handleDragEnter,
    handleDrop,
    endOfListPosition,
  } = useListEditorContext<FormField>();

  const { open: fieldFormOpenState, handleClose: closeFieldFormFn, handleOpen: openFieldFormFn } = useToggleState();

  const { useFetchCustomFieldTypes } = useFieldTypesAPI();
  const { customFieldTypes } = useFetchCustomFieldTypes();

  const createNewField = React.useCallback(
    (type: FIELD_TYPES) => {
      const newFieldOrder = endOfListPosition;
      let label = getFieldTypeWidgets(type)[0] ?? `New Field`;
      const customFieldTypeName = customFieldTypes[type];
      if (customFieldTypeName) label = `${customFieldTypeName}`;

      const newField = getDefaultField(currentFormConfiguration, type, newFieldOrder, label);

      createNewItem(newField);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFormConfiguration.id, createNewItem, endOfListPosition]
  );

  React.useEffect(() => {
    // When a new field is added, update the field count of form in provider to ensure current field count of the form is updated while avoiding loading the form from backend unnecessarily
    // Needed so that any components using the field count of a form can depend on the local state store without doing an unecessary fetch.
    // e.g. the service registration forms page checks if field_count > 0 to render form settings
    // Without this useEffect, we would have to fetch the form from backend again.
    if (itemsListState.savedItems.length > 0) {
      addForm({
        ...currentFormConfiguration,
        fields_count: itemsListState.savedItems.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsListState.savedItems.length]);

  return (
    <NewFieldDropTargetWrapper createNewField={createNewField}>
      <APIListProgressWrapper
        showLoader={isFetchingItems}
        loaderType="custom"
        customProgressLoader={<FieldsListSkeleton fieldsCount={currentFormConfiguration?.fields_count ?? 0} />}
        isAPIError={fetchItemsHasError}
        items={itemsListState.savedItems}
        httpResponseStatusCode={fetchItemsStatusCode}
        noDataMessage={<EmptyFieldsCTA form={currentFormConfiguration} />}
      >
        <FieldListItemDragLayer findItem={findItem} />
        <Stack direction="column" spacing={2}>
          {itemsListState.savedItems.map((fd) => (
            <div key={`field-id-${fd.id}`} onClick={() => selectItem(fd.id)}>
              <DragDropListItemWrapper
                currentItemId={fd.id}
                currentItemPosition={fd.order}
                itemType={DnDItemTypes.OS_FORM_FIELD_LIST_ITEM}
                handleDragEnter={handleDragEnter}
                handleDrop={handleDrop}
                dragHoveredItemStyle={{ background: COLORSCHEME.secondaryVariant }}
              >
                <FieldListItem
                  field={fd}
                  selected={fd.id === itemsListState.selectedItemId}
                  openFieldFormFn={openFieldFormFn}
                />
              </DragDropListItemWrapper>
              {fd.id === itemsListState.selectedItemId && (
                <FieldsListRightPanelForm
                  fieldData={fd}
                  closeFieldFormFn={closeFieldFormFn}
                  fieldFormOpenState={fieldFormOpenState && fd.id === itemsListState.selectedItemId}
                />
              )}
            </div>
          ))}
        </Stack>
      </APIListProgressWrapper>

      {itemsListState.itemCreationQueue.map((newItem, index) => (
        <>
          <FieldListItemSkeleton />
          <FieldsListRightPanelForm
            fieldData={newItem}
            closeFieldFormFn={closeFieldFormFn}
            fieldFormOpenState={false}
          />
        </>
      ))}

      {!creationQueueHasItems ? (
        <OSDropZone style={{ height: 100, marginTop: 10 }}>
          Drop items from the toolbar to create a new field
        </OSDropZone>
      ) : null}
    </NewFieldDropTargetWrapper>
  );
};

const NewFieldDropTargetWrapper = ({ createNewField, children }) => {
  return (
    <GenericDropTarget<IFieldDnDSelectorState>
      itemType={DnDItemTypes.OS_ADD_NEW_FORM_FIELD}
      handleDrop={({ newFieldType }) => createNewField(newFieldType)}
    >
      {children}
    </GenericDropTarget>
  );
};

const FieldsListSkeleton = ({ fieldsCount }) => {
  return (
    <>
      {Array(fieldsCount)
        .fill(0)
        .map((i, idx) => (
          <FieldListItemSkeleton key={`field-list-skeleton-${idx}`} />
        ))}
    </>
  );
};

const FieldListItemSkeleton = () => (
  <div className="os-form-builder-field-preview">
    <Skeleton animation="wave" variant="rectangular" height={180} />
  </div>
);

interface FieldListItemProps {
  field: FormField;
  selected: boolean;
  openFieldFormFn: any;
}

const FieldListItem = ({ field, selected, openFieldFormFn }: FieldListItemProps) => {
  const selectedCssClass = selected ? "selected" : "";

  return (
    <Paper className={`os-form-builder-field-preview ${selectedCssClass}`} elevation={6}>
      <Stack direction="column" spacing={2}>
        {selected && (
          <Stack direction="row" spacing={2}>
            <Tooltip title={<Typography>Field Settings</Typography>} placement="left-start">
              <FSettingsIcon
                className="os-form-builder-field-preview-action"
                color={COLORSCHEME.secondary}
                size={16}
                onClick={openFieldFormFn}
              />
            </Tooltip>
          </Stack>
        )}
        <FieldPreview label={field.label} field_type={field.field_type} />
      </Stack>
    </Paper>
  );
};

interface FieldsListRightPanelFormProps {
  fieldFormOpenState: boolean;
  closeFieldFormFn: () => void;
  fieldData: FormField;
}

const FieldsListRightPanelForm = React.memo(
  ({ fieldFormOpenState, closeFieldFormFn, fieldData }: FieldsListRightPanelFormProps) => {
    const { updateListAndSelectIfNewItem } = useListEditorContext<FormField>();

    return (
      <FixedRightSlidePanel open={fieldFormOpenState} onClose={closeFieldFormFn}>
        <FieldForm fieldData={fieldData} fieldSaveCallbackFn={updateListAndSelectIfNewItem} />
      </FixedRightSlidePanel>
    );
  }
);

const FieldListItemDragLayer = ({ findItem }: any) => {
  return (
    <ListItemDragLayer
      itemType={DnDItemTypes.OS_FORM_FIELD_LIST_ITEM}
      findItem={findItem}
      PreviewComponent={FieldDragLayerPreview}
    />
  );
};

interface FieldDragLayerPreviewProps {
  item: FormField;
}

const FieldDragLayerPreview = ({ item }: FieldDragLayerPreviewProps) => (
  <div className="os-form-builder-field-preview selected drag-preview">
    <FieldPreview label={item.label} field_type={item.field_type} />
  </div>
);
